import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@doccareplus/shared/lib';
import type { SubscriptionBannerInfo } from '../domain/subscriptionBanner';

export interface SubscriptionStatusBannerProps {
  info: SubscriptionBannerInfo | null;
  /** Whether the active role may reach the subscription-management screen. */
  canManageSubscription: boolean;
}

const TONE_CLASSES: Record<SubscriptionBannerInfo['variant'], string> = {
  trial: 'bg-[var(--warning-bg)] text-[var(--warning)]',
  pastDueGrace: 'bg-[var(--warning-bg)] text-[var(--warning)]',
  suspended: 'bg-[var(--danger-bg)] text-[var(--danger)]',
};

export function SubscriptionStatusBanner({ info, canManageSubscription }: SubscriptionStatusBannerProps) {
  const { t } = useTranslation('common');

  if (!info) return null;

  const message =
    info.variant === 'trial'
      ? t('subscription.banner.trial', { count: info.daysRemaining ?? 0 })
      : info.variant === 'pastDueGrace'
        ? t('subscription.banner.pastDueGrace', { count: info.daysRemaining ?? 0 })
        : t('subscription.banner.suspended');

  return (
    <div
      role="status"
      className={`flex items-center justify-center gap-2 px-4 py-2 text-[13px] font-medium ${TONE_CLASSES[info.variant]}`}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
      {canManageSubscription && (
        <Link to="/subscription" className="underline underline-offset-2 hover:opacity-80">
          {t('subscription.banner.manage')}
        </Link>
      )}
    </div>
  );
}
